/**
 * Voice Activity Detection (VAD) Provider Interface
 *
 * Base class for all VAD implementations.
 * Subclasses must implement the abstract methods.
 */

export class VADProvider {
  constructor(name) {
    this.name = name;
    this.initialized = false;
    this.isSpeaking = false;

    // Callbacks
    this.onSpeechStart = null;
    this.onSpeechEnd = null;
    this.onVADMisfire = null;
  }

  /**
   * Get the name of this provider
   * @returns {string}
   */
  getName() {
    return this.name;
  }

  /**
   * Check if this provider is supported on the current device
   * @param {DeviceCapabilities} capabilities
   * @returns {boolean}
   */
  static isSupported(capabilities) {
    throw new Error('isSupported() must be implemented by subclass');
  }

  /**
   * Initialize the VAD engine
   * Must be called before process()
   * @param {Object} options - Configuration options
   * @returns {Promise<void>}
   */
  async initialize(options = {}) {
    throw new Error('initialize() must be implemented by subclass');
  }

  /**
   * Process audio samples and detect speech
   * @param {Float32Array} samples - Input audio samples (normalized -1 to 1)
   * @param {number} sampleRate - Sample rate of the audio
   * @returns {boolean} - true if speech is detected
   */
  process(samples, sampleRate) {
    throw new Error('process() must be implemented by subclass');
  }

  /**
   * Check if speech is currently detected
   * @returns {boolean}
   */
  getSpeechState() {
    return this.isSpeaking;
  }

  /**
   * Check if the provider is ready to process audio
   * @returns {boolean}
   */
  isReady() {
    return this.initialized;
  }

  /**
   * Start/resume VAD processing
   */
  start() {
    throw new Error('start() must be implemented by subclass');
  }

  /**
   * Stop/pause VAD processing
   */
  stop() {
    throw new Error('stop() must be implemented by subclass');
  }

  /**
   * Clean up resources
   * @returns {Promise<void>}
   */
  async destroy() {
    throw new Error('destroy() must be implemented by subclass');
  }
}

export default VADProvider;
